import "@/lib/dayjs";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LicenseInfo } from "@mui/x-license-pro";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import App from "@/App";
import SentryErrorFallback from "@/Components/v2/molecule/SentryErrorFallback";
import Auth0MtxProvider from "@/context/auth0/auth0-mtx-provider";
import FeatureFlagProvider from "@/context/FeatureFlagProvider/use-feature-flags";
import ThemeProvider from "@/context/ThemeProvider";
import { EnvVar } from "@/env";
import { queryClient } from "@/lib/react-query";
import { AnalyticsProvider } from "@/Utils/analytics";

import Auth0TokenInjector from "./context/auth0/Auth0TokenInjector";
import { InfoProvider } from "./context/InfoProvider/InfoProvider";
import { ConfigProvider } from "./hooks/useConfig";
import ApiContextProvider from "./pages/hendren/api/context/ApiContextProvider";
import { ScenarioBuilderContextProvider } from "./pages/scenario-builder/api/context/ScenarioBuilderContextProvider";
import TenantIdObserver from "./TenantIdObserver";

LicenseInfo.setLicenseKey(
  "98027ccaba146ad8df062e0f64af54aeTz0xMTQzODMsRT0xNzgxMTM1OTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y"
);

const isProduction = EnvVar.envName === "production";

Sentry.init({
  dsn: "https://5ac89a83a55ec25989f8cc180cd382cf@o419463.ingest.us.sentry.io/4507217977606144",
  release: EnvVar.version,
  environment: EnvVar.envName,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
    Sentry.extraErrorDataIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
  ],
  enableLogs: true,
  tracesSampleRate: isProduction ? 0.1 : 1.0,
  replaysSessionSampleRate: isProduction ? 0 : 0.1,
  replaysOnErrorSampleRate: 1.0,
  // Enable distributed tracing
  tracePropagationTargets: [
    /^https:\/\/.*\.api\.growthos\.mutinex\.co/,
    /^https:\/\/.*\.maite\.mutinex\.co/,
    /^https:\/\/.*\.sbo\.mutinex\.co/,
    /^https:\/\/api\.growthos\.mutinex\.co/,
    /^https:\/\/maite\.mutinex\.co/,
    "localhost",
  ],
  beforeSend(event, hint) {
    const error = hint.originalException;

    // Filter out Auth0 login cancelled/popup closed errors
    if (error && typeof error === "object" && "error" in error && typeof error.error === "string") {
      const errorCode = error.error;
      if (errorCode === "login_required" || errorCode === "consent_required") {
        return null;
      }
    }

    // Filter out network errors from ad blockers or failed third-party scripts
    if (event.exception?.values?.[0]?.type === "NetworkError") {
      return null;
    }

    return event;
  },
});

const container = document.getElementById("root")!;
const root = createRoot(container);
root.render(
  <Sentry.ErrorBoundary fallback={SentryErrorFallback} showDialog={false}>
    <BrowserRouter>
      <TenantIdObserver />
      <Auth0MtxProvider>
        <Auth0TokenInjector />
        <QueryClientProvider client={queryClient}>
          <InfoProvider>
            <ThemeProvider>
              <AnalyticsProvider>
                <FeatureFlagProvider>
                  <ConfigProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <ApiContextProvider>
                        <ScenarioBuilderContextProvider>
                          <App />
                        </ScenarioBuilderContextProvider>
                      </ApiContextProvider>
                    </LocalizationProvider>
                  </ConfigProvider>
                </FeatureFlagProvider>
              </AnalyticsProvider>
            </ThemeProvider>
          </InfoProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </Auth0MtxProvider>
    </BrowserRouter>
  </Sentry.ErrorBoundary>
);
